<template>
  <vs-card
    class="mb-4 signer-card"
    :style="{ borderLeft: `4px solid ${signerColor}` }"
  >
    <div class="vx-row">
      <!-- Header con avatar de color -->
      <div class="vx-col w-full mb-3">
        <div class="signer-header flex items-center">

          <!-- Drag handle + Avatar -->
          <div class="drag-handle flex items-center gap-3">
            <feather-icon
              icon="MenuIcon"
              class="cursor-move shrink-0"
              svgClasses="w-4 h-4"
              :style="{ color: signerColor + 'aa' }"
            />

            <!-- Avatar circular con color del firmante -->
            <div
              class="signer-avatar"
              :style="{
                background: signerColor + '22',
                border: `2px solid ${signerColor}`,
                color: signerColor,
              }"
            >
              <span v-if="signer.name" class="avatar-initials">
                {{ signer.name.trim().split(' ').slice(0,2).map(w => w[0]).join('').toUpperCase() }}
              </span>
              <feather-icon v-else icon="UserIcon" svgClasses="w-4 h-4" />
            </div>

            <!-- Nombre y número de firmante -->
            <div class="flex flex-col leading-tight">
              <span
                class="signer-order font-bold text-base leading-tight"
                :style="{ color: signerColor }"
              >
                Firmante {{ (displayIndex !== null ? displayIndex : index) + 1 }}
              </span>
              <span v-if="signer.name" class="text-xs text-on-surface-variant truncate max-w-[140px]">
                {{ signer.name }}
              </span>
            </div>
          </div>

          <!-- Botón eliminar -->
          <vs-button
            type="flat"
            color="danger"
            icon-pack="feather"
            icon="icon-x"
            class="ml-auto"
            @click="$emit('remove-signer', index)"
          />
        </div>
      </div>

      <!-- Correo electrónico -->
      <div class="vx-col w-full mb-2">
        <vue-simple-suggest
          ref="auto_suggest"
          placeholder="Correo electrónico"
          v-model="signer.email"
          :list="emailSuggestions"
          @input="onInputChange"
          @select="onSelectSuggestion"
          :min-length="3"
        >
          <vs-input
            :danger="focusValidation && (!$v.signer.email.email || !$v.signer.email.required)"
            val-icon-danger="icon-x"
            val-icon-pack="feather"
            @blur="validationEval"
            @focus="focusValidation = true"
            class="w-full"
            label-placeholder="Correo electrónico"
            v-model.trim="signer.email"
          />
        </vue-simple-suggest>
        <div class="text-danger" v-if="focusValidation && !$v.signer.email.required">
          El campo no puede estar vacío
        </div>
        <div class="text-danger" v-if="focusValidation && !$v.signer.email.email">
          El correo es inválido
        </div>
      </div>

      <!-- Nombre -->
      <div class="vx-col w-full mb-2">
        <vs-input
          :danger="focusValidation && !$v.signer.name.required"
          val-icon-danger="icon-x"
          val-icon-pack="feather"
          @blur="validationEval"
          @focus="focusValidation = true"
          class="w-full"
          label-placeholder="Nombre completo"
          v-model.trim="signer.name"
        />
        <div class="text-danger" v-if="focusValidation && !$v.signer.name.required">
          El campo no puede estar vacío
        </div>
      </div>

      <!-- Restricción por RFC o CURP -->
      <div class="vx-col w-full mb-2">
        <vx-input-group>
          <div class="flex">
            Restringe la firma por RFC o CURP
            <vx-tooltip class="d-inline-block" text="Más información" position="top">
              <a target="_blank" href="https://ayuda.verificamex.com/article/61-restringir-con-rfc-o-curp/">
                <feather-icon
                  icon="HelpCircleIcon"
                  class="top-1 mx-2 text-primary"
                  svgClasses="w-5 h-5 text-primary"
                ></feather-icon>
              </a>
            </vx-tooltip>
            <vs-switch v-model="signer.restrictSign" @change="onRestrictSignChange" />
          </div>

          <div class="mt-4 mb-4" v-if="signer.restrictSign">
            <p class="mb-3">
              Solo el firmante con el RFC o CURP que ingreses podrá firmar el documento.
            </p>
            <vs-radio
              class="mr-4"
              v-model="signer.restrictSelector"
              vs-value="RFC"
              :vs-name="Math.random()"
              @change="onRestrictSelectorChange"
            >
              RFC (e.firma SAT)
            </vs-radio>
            <vs-radio
              v-model="signer.restrictSelector"
              vs-value="CURP"
              :vs-name="Math.random()"
              @change="onRestrictSelectorChange"
            >
              CURP (Firma con identidad y FIREL)
            </vs-radio>

            <!-- Campo RFC o CURP -->
            <vs-input
              :danger="!rfcOrCurpValid"
              val-icon-danger="icon-x"
              val-icon-pack="feather"
              class="w-full mt-5"
              :label-placeholder="signer.restrictSelector === 'RFC' ? 'Restringir con RFC. Solo para e.firma SAT.' : 'Restringir con CURP. Solo para Firma Electrónica Simple y FIREL.'"
              v-model.trim="signer.rfc"
            />
            <div class="text-danger" v-if="!rfcOrCurpValid">
              {{ signer.restrictSelector === 'RFC' ? 'El RFC es inválido' : 'El CURP es inválido' }}
            </div>
          </div>
        </vx-input-group>

        <vx-input-group>
          <div class="vx-col w-full mb-2">
            <!-- Selector de teléfono con código de país -->
            <div class="phone-input-container">
              <label class="text-sm text-gray-600 mb-1 block">Notificación por WhatsApp (opcional)</label>
              <vue-tel-input
                v-model="phoneDisplay"
                @input="onPhoneInput"
                :inputOptions="telInputOptions.inputOptions"
                :defaultCountry="telInputOptions.defaultCountry"
                :enabledFlags="telInputOptions.enabledFlags"
                :enabledCountryCode="telInputOptions.enabledCountryCode"
                :preferredCountries="telInputOptions.preferredCountries"
                :onlyCountries="telInputOptions.onlyCountries"
                :invalidMsg="telInputOptions.invalidMsg"
                :mode="telInputOptions.mode"
                :autocomplete="'tel'"
                :inputClasses="{'input-error': !phoneValid}"
                :dropdownOptions="{showDialCodeInSelection: true}"
                class="vue-tel-input-custom"
              />
            </div>
            <div class="text-danger" v-if="!phoneValid">
              El número de teléfono es inválido
            </div>
            <small class="mb-0 mt-2 block">
              Puedes agregar el teléfono del firmante para notificar mediante Whatsapp.
            </small>
          </div>
        </vx-input-group>
      </div>

      <!-- Posicionamiento Personalizado: acciones por firmante -->
      <div class="vx-col w-full mb-2" v-if="signPosition === 'CUSTOM'">
        <div
          class="signer-actions-panel"
          :style="{
            borderColor: signerColor + '55',
            background: signerColor + '0d',
          }"
        >
          <!-- Contenido en una sola línea -->
          <div class="flex flex-wrap items-center justify-between w-full gap-2">
            
            <!-- Izquierda: Estado -->
            <div class="flex items-center gap-2">
              <span v-if="signMode === 'CLASSIC'"
                class="signatures-pill"
                :style="{ background: signerColor + '22', color: signerColor }"
              >
                Hoja de firmas colocada
              </span>
              <span v-else
                class="signatures-pill"
                :style="{ background: signerColor + '22', color: signerColor }"
              >
                {{ signaturesCount }} {{ signaturesCount === 1 ? 'firma asignada' : 'firmas asignadas' }}
              </span>
              
              <span class="placement-status" v-if="signMode !== 'CLASSIC'">
                <feather-icon v-if="signer.pos_x !== null" icon="CheckCircleIcon" svgClasses="w-3 h-3 text-success" />
                <feather-icon v-else icon="AlertCircleIcon" svgClasses="w-3 h-3 text-warning" />
                <small :class="signer.pos_x === null ? 'text-warning' : 'text-success'">
                  {{ signer.pos_x === null ? 'Pendiente' : 'Colocada' }}
                </small>
              </span>
            </div>

            <!-- Derecha: Botones -->
            <div class="flex items-center gap-2" v-if="signMode !== 'CLASSIC'">
              <vx-tooltip :text="hasPdf ? 'Colocar recuadro de firma' : 'Primero sube un PDF'" position="top">
                <button
                  class="action-btn place-btn"
                  :style="hasPdf ? { borderColor: signerColor + 'aa', color: signerColor } : {}"
                  @click="hasPdf ? $emit('place-signer', signer.email) : null"
                  :class="{'opacity-40 cursor-not-allowed': !hasPdf}"
                >
                  <feather-icon icon="MapPinIcon" svgClasses="w-3 h-3 mr-1" />
                  Colocar
                </button>
              </vx-tooltip>

              <button
                v-if="signaturesCount > 0"
                class="action-btn clear-btn"
                @click="$emit('clear-signer', signer.email)"
                title="Quitar todas las firmas"
              >
                <feather-icon icon="Trash2Icon" svgClasses="w-3 h-3" />
              </button>
            </div>

          </div>
        </div>
      </div>
    </div>
  </vs-card>
</template>

<script>
import {required, email, helpers} from 'vuelidate/lib/validators';
import VueSimpleSuggest from 'vue-simple-suggest';
import { VueTelInput } from 'vue-tel-input'; // Importamos la librería
import 'vue-simple-suggest/dist/styles.css';
import {throttle} from "lodash";

const rfcRegexValidation = helpers.regex(
  'rfcRegexValidation',
  /^([A-ZÑ&]{3,4})(\d{2}(?:0[1-9]|1[0-2])(?:0[1-9]|[12]\d|3[01]))([A-Z\d]{2})([A\d])$/
);
const curpRegexValidation = helpers.regex(
  'curpRegexValidation',
  /^([A-Z][AEIOUX][A-Z]{2}\d{2}(?:0[1-9]|1[0-2])(?:0[1-9]|[12]\d|3[01])[HM](?:AS|BCS|CC|CL|CM|CS|CH|DF|DG|GT|HG|JC|MC|MN|MS|NT|NL|OC|PL|QT|QR|SP|SL|SR|TC|TS|VZ|YN|ZS|NE)[B-DF-HJ-NP-TV-Z]{3}[A-Z\d])(\d)$/
);

// Misma paleta que PdfSignaturePlacer para coherencia visual
const SIGNER_COLORS = [
  '#4173FF', '#FF6B6B', '#00C49F', '#FFBB28',
  '#A855F7', '#F97316', '#14B8A6', '#EC4899',
];

export default {
  name: 'SignerCard',
  props: {
    signer: {
      type: Object,
      required: true,
    },
    index: {
      type: Number,
      required: true,
    },
    sortable: {
      type: Boolean,
      default: false,
    },
    signPosition: {
      type: String,
      default: 'NONE',
    },
    signMode: {
      type: String,
      default: 'PDF_SIGNATURE',
    },
    allSigners: {
      type: Array,
      default: () => [],
    },
    displayIndex: {
      type: Number,
      default: null,
    },
    hasPdf: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    VueSimpleSuggest,
    VueTelInput // Registramos el componente
  },
  data() {
    return {
      focusValidation: false,
      emailSuggestions: [],
      phoneDisplay: '', // Para mostrar el teléfono formateado
      phoneData: {
        number: '',
        isValid: true,
        countryCode: '+52', // Código de México por defecto
        formatted: ''
      },

      // Opciones para vue-tel-input
      telInputOptions: {
        mode: 'international',
        defaultCountry: 'MX', // México por defecto
        disabledFetchingCountry: false,
        enabledCountryCode: true,
        enabledFlags: true,
        preferredCountries: ['MX', 'US', 'ES', 'CO', 'AR', 'CL', 'PE'],
        onlyCountries: [], // Dejar vacío para incluir todos los países
        inputOptions: {
          placeholder: 'Ingrese el número de teléfono',
          showDialCode: true,
        },
        invalidMsg: 'Número de teléfono inválido'
      }
    };
  },
  created() {
    this.debouncedSearch = throttle(this.searchSigners, 1000);

    // Si ya hay un número de teléfono, lo configuramos para el componente
    if (this.signer.phone) {
      // Simplemente asignamos el valor para que el componente lo maneje
      this.phoneDisplay = this.signer.phone;
    }

    // Inicializar campos de posicionamiento personalizado con null si no existen (requerido para flujo manual)
    if (this.signer.pos_x === undefined) this.$set(this.signer, 'pos_x', null);
    if (this.signer.pos_y === undefined) this.$set(this.signer, 'pos_y', null);
    if (this.signer.width === undefined) this.$set(this.signer, 'width', 140);
    if (this.signer.height === undefined) this.$set(this.signer, 'height', 80);
    if (this.signer.page === undefined) this.$set(this.signer, 'page', 1);
  },
  computed: {
    rfcOrCurpValid() {
      if (!this.signer.restrictSign) return true;
      if (this.signer.restrictSelector === 'RFC') {
        return rfcRegexValidation(this.signer.rfc);
      } else {
        return curpRegexValidation(this.signer.rfc);
      }
    },
    phoneValid() {
      return this.phoneData.isValid;
    },
    isPosXValid() {
      return this.signPosition !== 'CUSTOM' || (typeof this.signer.pos_x === 'number' && this.signer.pos_x >= 0);
    },
    isPosYValid() {
      return this.signPosition !== 'CUSTOM' || (typeof this.signer.pos_y === 'number' && this.signer.pos_y >= 0);
    },
    isWidthValid() {
      return this.signPosition !== 'CUSTOM' || (typeof this.signer.width === 'number' && this.signer.width > 0);
    },
    isHeightValid() {
      return this.signPosition !== 'CUSTOM' || (typeof this.signer.height === 'number' && this.signer.height > 0);
    },
    isPageValid() {
      return this.signPosition !== 'CUSTOM' || (typeof this.signer.page === 'number' && this.signer.page >= 0);
    },
    signaturesCount() {
      if (!this.signer.email) return 0;
      return this.allSigners.filter(s => s.email === this.signer.email && s.pos_x !== null).length;
    },
    signerColor() {
      const idx = (this.displayIndex !== null ? this.displayIndex : this.index);
      return SIGNER_COLORS[idx % SIGNER_COLORS.length];
    },
  },
  validations: {
    signer: {
      name: {
        required,
      },
      email: {
        required,
        email,
      },
      rfc: {
        rfcOrCurpValid(value) {
          if (!this.signer.restrictSign) return true;
          if (this.signer.restrictSelector === 'RFC') {
            return rfcRegexValidation(value);
          } else {
            return curpRegexValidation(value);
          }
        },
      },
    },
  },
  methods: {
    // Método para manejar el cambio en el input del teléfono
    onPhoneInput(formattedNumber, { number, isValid, country }) {
      // Validación manual de respaldo para México (10 dígitos después del +52)
      // Esto es necesario para claves LADA nuevas como la 220 que la librería vieja no reconoce
      let manualValid = isValid;
      
      if (!isValid && country && country.iso2 === 'MX') {
        // Limpiamos el número para quedarnos solo con dígitos
        const digitsOnly = formattedNumber.replace(/\D/g, '');
        // Si tiene 12 dígitos (52 + 10 del número) o 10 dígitos (sin el 52)
        if (digitsOnly.length === 12 && digitsOnly.startsWith('52')) {
          manualValid = true;
        } else if (digitsOnly.length === 10) {
          manualValid = true;
        }
      }

      this.phoneData = {
        number: number,
        countryCode: country && country.dialCode ? `+${country.dialCode}` : null,
        isValid: manualValid,
        formatted: formattedNumber
      };

      // Actualizamos el valor en el objeto signer
      if (manualValid) {
        this.$set(this.signer, 'phone', this.phoneData.formatted);
      } else {
        // En este caso, mantenemos el valor para que el usuario lo pueda corregir
        this.$set(this.signer, 'phone', formattedNumber);
      }
    },
    validationEval() {
      this.focusValidation = true;
    },
    onRestrictSignChange() {
      if (!this.signer.restrictSign) {
        this.signer.rfc = '';
      }
    },
    onRestrictSelectorChange() {
      this.signer.rfc = '';
    },
    onInputChange(value) {
      if (value.length < 3) {
        return;
      }
      if (value.trim() === '') {
        this.emailSuggestions = [];
      } else {
        this.debouncedSearch(value);
      }
    },
    searchSigners(query) {
      this.$store.dispatch("document/searchSigners", {query}).then(
        response => {
          console.log(response)
          this.emailSuggestions = response.data
        }
      ).catch(error => {
        console.log(error)
      })
    },
    onSelectSuggestion(selected) {
      this.$store.dispatch("document/searchSignersByEmail", {email: selected}).then(
        response => {
          console.log(response);
          this.signer.name = response.data[0].attributes.name;
          this.signer.email = response.data[0].attributes.email;

          // Si el firmante tiene teléfono, lo asignamos también
          if (response.data[0].attributes.phone) {
            this.phoneDisplay = response.data[0].attributes.phone;
            this.signer.phone = response.data[0].attributes.phone;
          }
        }
      )
    }
  },
  watch: {
    // Sincronizar el componente de teléfono si el valor cambia externamente (ej. desde el modal)
    'signer.phone': function(newVal) {
      if (newVal !== this.phoneDisplay) {
        this.phoneDisplay = newVal;
      }
    }
  }
};
</script>

<style scoped>
.text-danger {
  color: red;
  font-size: 0.8rem;
}
.text-xs {
  font-size: 0.75rem;
}
.drag-handle {
  cursor: move;
}
.signer-card {
  transition: all 0.2s ease;
  border-left: 4px solid transparent; /* sobreescrito por :style */
  overflow: hidden;
}

.signer-card.dragging {
  opacity: 0.8;
  transform: scale(1.02);
}

.drag-handle {
  cursor: move;
  display: flex;
  align-items: center;
}

.signer-header {
  align-items: center;
}

.signer-order {
  line-height: 1.1;
}

/* Avatar circular del firmante */
.signer-avatar {
  width: 36px;
  height: 36px;
  min-width: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 0.78rem;
  transition: transform 0.2s ease;
}

.signer-avatar:hover {
  transform: scale(1.08);
}

.avatar-initials {
  letter-spacing: 0.5px;
  line-height: 1;
}

/* Estilos específicos para vue-tel-input */
.vue-tel-input-custom {
  border-radius: 4px;
  border: 1px solid #ccc;
  font-family: inherit;
  width: 100%;
}

.vue-tel-input-custom.vue-tel-input-focused {
  border-color: #4173FF;
  box-shadow: 0 0 0 0.2rem rgba(65, 115, 255, 0.25);
}

.vue-tel-input-custom .input-error {
  border-color: #e74c3c;
  box-shadow: 0 0 0 0.2rem rgba(231, 76, 60, 0.25);
}

.phone-input-container {
  margin-bottom: 0.5rem;
}

/* Mejoras para el dropdown de países */
:deep(.vti__dropdown) {
  border-radius: 4px 0 0 4px;
  transition: all 0.3s ease;
}

:deep(.vti__dropdown:hover) {
  background-color: #f8f9fa;
}

:deep(.vti__dropdown-list) {
  max-height: 240px;
  overflow-y: auto;
  border-radius: 4px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  border: 1px solid #e1e4e8;
}

:deep(.vti__dropdown-list-item) {
  padding: 10px 10px;
  transition: background-color 0.2s;
}

:deep(.vti__dropdown-list-item:hover) {
  background-color: #f1f5f9;
}

:deep(.vti__dropdown-list-item.highlighted) {
  background-color: #e6f0ff;
}

/* Acciones por firmante en modo CUSTOM */
.signer-actions-panel {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 10px 12px;
  border-radius: 10px;
  background: rgba(65, 115, 255, 0.05);
  border: 1px dashed rgba(65, 115, 255, 0.25);
}

.signer-actions-info {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  font-size: 0.82rem;
}

.signatures-pill {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 12px;
  background: rgba(65, 115, 255, 0.1);
  color: rgba(65, 115, 255, 1);
  font-size: 0.8rem;
  font-weight: 600;
  letter-spacing: 0.3px;
}

.placement-status {
  display: flex;
  align-items: center;
  gap: 3px;
  margin-left: auto;
}

.signer-actions-buttons {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}

.action-btn {
  display: inline-flex;
  align-items: center;
  font-size: 0.78rem;
  font-weight: 600;
  padding: 5px 12px;
  border-radius: 20px;
  border: 1.5px solid;
  cursor: pointer;
  transition: all 0.2s ease;
  background: transparent;
  font-family: inherit;
}

.action-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.place-btn {
  border-color: rgba(65, 115, 255, 0.5);
  color: rgba(65, 115, 255, 1);
}

.place-btn:hover:not(:disabled) {
  background: rgba(65, 115, 255, 0.1);
  border-color: rgba(65, 115, 255, 0.8);
}

.clear-btn {
  border-color: rgba(231, 76, 60, 0.4);
  color: rgba(231, 76, 60, 1);
}

.clear-btn:hover {
  background: rgba(231, 76, 60, 0.08);
  border-color: rgba(231, 76, 60, 0.7);
}

/* Estilos legacy (mantenidos para compatibilidad) */
.text-success {
  color: #28c76f;
  font-weight: 600;
}

.text-warning {
  color: #ff9f43;
}
</style>
